import { getUiLocale } from '../../utils/uiTranslation';
import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './PublicApprovalPage.css';

const PublicApprovalPage = () => {
  useUiLanguage();
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState(null);
  const [actionModal, setActionModal] = useState(null); // 'approve' | 'reject'
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [processedState, setProcessedState] = useState(null); // 'success' | 'already_processed'
  const [processedAction, setProcessedAction] = useState(null); // 'approved' | 'rejected'

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

  useEffect(() => {
    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/manpower/validate-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (data.success) {
        setRequest(data.request);
        if (data.already_processed) {
          setProcessedState('already_processed');
          // Try to infer status from request if available
          setProcessedAction(data.request.internalApproval?.status || 'processed');
        }
      } else {
        setError(data.error || 'Invalid or expired token');
      }
    } catch (err) {
      setError(uiText('Failed to validate token. Please try again later.'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async () => {
    if (!actionModal) return;
    if (actionModal === 'reject' && !notes.trim()) {
      alert(uiText('Please provide a reason for rejection.'));
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/manpower/process-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          action: actionModal,
          notes: notes
        })
      });

      const data = await response.json();

      if (data.success) {
        setProcessedState('success');
        setProcessedAction(actionModal === 'approve' ? 'approved' : 'rejected');
        setActionModal(null);
      } else {
        alert(data.error || 'Failed to process request');
      }
    } catch (err) {
      alert(uiText('An error occurred. Please try again.'));
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="approval-container">
        <div className="approval-message">{uiText("Loading request details...")}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="approval-container">
        <div className="approval-header">
          <h1 className="approval-logo">{uiText("JOBSEEQR")}</h1>
          <p className="approval-subtitle">{uiText("Approval Portal")}</p>
        </div>
        <div className="approval-card">
          <div className="approval-state-container approval-state-error">
            <h2>{uiText("Access Denied")}</h2>
            <p className="approval-state-p">{uiText(error)}</p>
          </div>
        </div>
      </div>
    );
  }

  if (processedState) {
    return (
      <div className="approval-container">
        <div className="approval-header">
          <h1 className="approval-logo">{uiText("JOBSEEQR")}</h1>
          <p className="approval-subtitle">{uiText("Approval Portal")}</p>
        </div>
        <div className="approval-card">
          <div className="approval-state-container approval-state-success">
            <h2>
              {processedState === 'already_processed' ? uiText("Request Already Processed") : uiText("Action Recorded Successfully")}
            </h2>
            <p className="approval-state-p">{uiText("This request has been ")}<strong>{processedAction}</strong>.
            </p>
            <p style={{ marginTop: 20 }} className="approval-state-p">{uiText("You can close this window now.")}</p>
          </div>
        </div>
      </div>
    );
  }

  // Formatting helpers
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(getUiLocale(), {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatEmploymentType = (value) => {
    if (!value) return '';
    if (value === 'full-time') return uiText('Full-time');
    if (value === 'part-time') return uiText('Part-time');
    if (value === 'contract') return uiText('Contract');
    return value;
  };

  const formatUrgency = (value) => {
    switch (value) {
      case 'immediate': return uiText('Immediate');
      case '30_days': return uiText('Within 30 Days');
      case '90_days': return uiText('Within 90 Days');
      case 'not_urgent': return uiText('Not Urgent');
      default: return value || '';
    }
  };

  const formatBudgetStatus = (value) => {
    switch (value) {
      case 'budgeted': return uiText('Budgeted');
      case 'unbudgeted': return uiText('Unbudgeted');
      case 'pending': return uiText('Pending Finance Approval');
      default: return value || '';
    }
  };

  const formatNationalityPref = (value) => {
    switch (value) {
      case 'local': return uiText('Local Candidates Preferred');
      case 'any': return uiText('Open to All Nationalities');
      case 'specific': return uiText('Specific Nationality Required');
      default: return value || '';
    }
  };

  const formatVisaRequirement = (value) => {
    switch (value) {
      case 'local': return uiText('Local Hire Only (No Sponsorship Needed)');
      case 'transfer': return uiText('Work Permit Transfer Acceptable');
      case 'new': return uiText('New Work Permit / Sponsorship Required');
      default: return value || '';
    }
  };

  const formatExperience = (value) => {
    switch (value) {
      case 'entry': return uiText('Entry Level (0-2 years)');
      case 'mid': return uiText('Mid Level (3-5 years)');
      case 'senior': return uiText('Senior (6-10 years)');
      case 'expert': return uiText('Expert (10+ years)');
      default: return value || '';
    }
  };

  const requestTypeLabel = request?.requestType === 'replacement' ? 'Replacement' : 'New Position';

  return (
    <div className="approval-container">
      <div className="approval-header">
        <h1 className="approval-logo">{uiText("JOBSEEQR")}</h1>
        <p className="approval-subtitle">{uiText("Approval Portal")}</p>
      </div>

      <div className="approval-card">
        <div className="approval-card-header">
          <div className="approval-label">{uiText("Manpower request")}</div>
          <h2 className="approval-title">{request?.position?.title || uiText("Untitled Position")}</h2>
          <span className="approval-status-badge">{uiText("Awaiting Finance/GM Approval")}</span>
        </div>

        <div className="approval-content">
          <div className="approval-detail-grid">
            <div className="approval-section">
              <div className="approval-label">{uiText("Department")}</div>
              <div className="approval-value">{request?.submitter?.department || 'N/A'}</div>
            </div>
            <div className="approval-section">
              <div className="approval-label">{uiText("Location")}</div>
              <div className="approval-value">{request?.submitter?.location || 'N/A'}</div>
            </div>
            <div className="approval-section">
              <div className="approval-label">{uiText("Request Type")}</div>
              <div className="approval-value">{uiText(requestTypeLabel)}</div>
            </div>
            {request?.requestType === 'replacement' && (
              <div className="approval-section">
                <div className="approval-label">{uiText("Employee Being Replaced")}</div>
                <div className="approval-value">{request?.replacementDetails?.employeeName || 'N/A'}</div>
              </div>
            )}
            {request?.requestType === 'new' && request?.budgetStatus && (
              <div className="approval-section">
                <div className="approval-label">{uiText("Budget Status")}</div>
                <div className="approval-value">{uiText(formatBudgetStatus(request?.budgetStatus))}</div>
              </div>
            )}
            <div className="approval-section">
              <div className="approval-label">{uiText("Headcount")}</div>
              <div className="approval-value">{request?.position?.headcount || 1}</div>
            </div>
            <div className="approval-section">
              <div className="approval-label">{uiText("Employment Type")}</div>
              <div className="approval-value">{formatEmploymentType(request?.position?.type) || 'N/A'}</div>
            </div>
            <div className="approval-section">
              <div className="approval-label">{uiText("Urgency")}</div>
              <div className="approval-value">{formatUrgency(request?.position?.urgency) || 'N/A'}</div>
            </div>
            {request?.position?.gradeCode && (
              <div className="approval-section">
                <div className="approval-label">{uiText("Grade / Band")}</div>
                <div className="approval-value">{request?.position?.gradeCode}</div>
              </div>
            )}
            {request?.position?.salaryRange && (
              <div className="approval-section">
                <div className="approval-label">{uiText("Salary Range")}</div>
                <div className="approval-value">{request?.position?.salaryRange}</div>
              </div>
            )}
            {request?.gccCompliance?.nationalityPref && (
              <div className="approval-section">
                <div className="approval-label">{uiText("Nationality Preference")}</div>
                <div className="approval-value">{uiText(formatNationalityPref(request?.gccCompliance?.nationalityPref))}</div>
              </div>
            )}
            {request?.gccCompliance?.visaRequirement && (
              <div className="approval-section">
                <div className="approval-label">{uiText("Visa Requirement")}</div>
                <div className="approval-value">{uiText(formatVisaRequirement(request?.gccCompliance?.visaRequirement))}</div>
              </div>
            )}
          </div>

          <div className="approval-section">
            <div className="approval-label">{uiText("Business Justification")}</div>
            <div className="approval-rich-value">{request?.justification || 'N/A'}</div>
          </div>

          {request?.costCenter && (
            <div className="approval-section">
              <div className="approval-label">{uiText("Cost Center / Budget Code")}</div>
              <div className="approval-value">{request?.costCenter}</div>
            </div>
          )}

          {(request?.optional?.skills || request?.optional?.experience || request?.optional?.reportingTo) && (
            <div className="approval-section">
              <div className="approval-label">{uiText("Additional Details")}</div>
              <div className="approval-rich-value">
                {request?.optional?.skills && (
                  <div><strong>{uiText("Required Skills:")}</strong> {request?.optional?.skills}</div>
                )}
                {request?.optional?.experience && (
                  <div><strong>{uiText("Experience Level:")}</strong> {uiText(formatExperience(request?.optional?.experience))}</div>
                )}
                {request?.optional?.reportingTo && (
                  <div><strong>{uiText("Reports To:")}</strong> {request?.optional?.reportingTo}</div>
                )}
              </div>
            </div>
          )}

          <div className="approval-meta">
            <div className="approval-label">{uiText("Requested By")}</div>
            <div className="approval-value">{request?.submitter?.name} ({request?.submitter?.email})</div>
            {formatDate(request?.submittedAt) && (
              <div className="approval-meta-date">{formatDate(request?.submittedAt)}</div>
            )}
          </div>

        </div>

        <div className="approval-actions">
          <button
            className="approval-btn approval-btn-danger"
            onClick={() => setActionModal('reject')}
          >{uiText("Reject Request")}</button>
          <button
            className="approval-btn approval-btn-primary"
            onClick={() => setActionModal('approve')}
          >{uiText("Approve Request")}</button>
        </div>
      </div>

      {/* Action Dialog */}
      {actionModal && (
        <div className="approval-modal-overlay">
          <div className="approval-modal-content">
            <h3 className="approval-modal-title">
              {actionModal === 'approve' ? uiText("Confirm Approval") : uiText("Reject Request")}
            </h3>

            <p style={{ color: '#64748b', marginBottom: '12px' }}>
              {actionModal === 'approve'
                ? uiText("Are you sure you want to approve this manpower request?")
                : uiText("Please provide a reason for rejecting this request.")}
            </p>

            <div className="approval-label">{actionModal === 'approve' ? uiText("Comments (Optional)") : uiText("Reason (Required)")}</div>
            <textarea
              className="approval-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={actionModal === 'approve' ? uiText("Add any notes...") : uiText("Why is this request being rejected?")}
              autoFocus
            />

            <div className="approval-modal-actions">
              <button
                className="approval-btn approval-btn-secondary"
                onClick={() => {
                  setActionModal(null);
                  setNotes('');
                }}
                disabled={processing}
              >{uiText("Cancel")}</button>
              <button
                className="approval-btn"
                style={{ '--light-control-fill': actionModal === 'approve' ? '#10b981' : '#ef4444',
                  backgroundColor: actionModal === 'approve' ? '#10b981' : '#ef4444',
                  color: 'white'
                }}
                onClick={handleProcess}
                disabled={processing}
                data-light-control={actionModal === 'approve' ? "filled" : "filled"}>
                {processing ? uiText("Processing...") : (actionModal === 'approve' ? uiText("Confirm Approval") : uiText("Reject Request"))}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicApprovalPage;
